// Crew Mode — Supervisor correction v0
// (docs/crew-mode/prd/crew-mode-supervisor-correction-v0.md), extended by the
// pre-validation PR 3 (findings #54, #55, #66).
//
// One "Corregir…" entry point per row of a feed / mortality / weight-sample /
// water record. The dialog shows the row's correction history and offers
// three audited actions, each with a required reason and a review step:
//   - Corregir valores: value fields + recordedAt + notes (+ lengthMm);
//   - Mover a otro lote/unidad: re-bind a wrong-pond entry (server re-runs
//     the I-8 C3 guard);
//   - Eliminar: an audited delete — the server keeps the full prior row.
// editor/admin only (PRD §11 criterion 10). No derived figure is shown (O-1).

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { api, ApiError, type CrewDataCorrectionRecord } from '../../utils/api'
import { logEvent } from '../../utils/log'
import { useT, type TranslationKey } from '../../i18n'
import { useStoredPrincipal } from '../../hooks/useStoredPrincipal'
import type { InventoryItem } from '../../types/inventory'
import type { Lot } from '../../types/lot'
import { formatStoredDateTimeAsLocal } from '../../utils/dateNormalization'
import { farmDateTimeInputValue, farmTimeZone, farmWallClockToInstant } from '../../utils/farmTime'
import { feedLogApi } from '../../utils/feedLogApi'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import { actorLabel, useActorNames } from '../../utils/actorNames'
import { isMixItem } from '../../utils/feedMix'
import {
  CREW_CORRECTION_FIELDS,
  FEEDING_RESPONSE_LABEL_KEY,
  FEEDING_RESPONSE_OPTIONS,
  MORTALITY_CAUSE_LABEL_KEY,
  OBSERVED_CAUSE_OPTIONS,
  STOCK_METHOD_LABEL_KEY,
  STOCK_METHOD_OPTIONS,
  readCorrectionFieldValue,
  type CrewCorrectionFieldSpec,
  type CrewCorrectionResource
} from './crewCorrectionFields'

type ServerRow = Record<string, unknown> & {
  id: string
  farmId?: string
  lotId?: string
  productionUnitId?: string
  updatedAt?: string
  metadata?: Record<string, unknown>
}

type UnitOption = { id: string; name: string }

type Props = Readonly<{
  resource: CrewCorrectionResource
  entityId: string
  /** Feed catalog, units and lots are loaded for this farm. */
  farmId?: string
  onDone?: () => void
  disabled?: boolean
  /** Host-specific delete (water rows live in the local water table). When
   *  absent, feed / mortality / weight-sample use their client API. */
  onDelete?: (reason: string) => Promise<void>
}>

const fetchRow = (resource: CrewCorrectionResource, id: string): Promise<ServerRow> => {
  switch (resource) {
    case 'water-log':
      return api.waterLogs.get(id) as unknown as Promise<ServerRow>
    case 'feed-log':
      return api.feedLogs.get(id) as unknown as Promise<ServerRow>
    case 'mortality-log':
      return api.mortalityLogs.get(id) as unknown as Promise<ServerRow>
    case 'weight-sample-log':
      return api.weightSampleLogs.get(id) as unknown as Promise<ServerRow>
    case 'fish-stock-log':
      return api.fishStockLogs.get(id) as unknown as Promise<ServerRow>
  }
}

// The delete is queued in the outbox (works offline) and, when online, synced
// right away; wait for that so the host's reload no longer sees the row.
const defaultDelete = async (resource: CrewCorrectionResource, id: string, reason: string): Promise<void> => {
  const result =
    resource === 'feed-log'
      ? await feedLogApi.delete(id, { reason })
      : resource === 'mortality-log'
        ? await mortalityLogApi.delete(id, { reason })
        : resource === 'weight-sample-log'
          ? await weightSampleLogApi.delete(id, { reason })
          : null
  if (!result) throw new Error('water-log delete needs the host screen (onDelete)')
  await result.syncPromise?.catch(() => undefined)
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 24,
  zIndex: 1000,
  overflowY: 'auto'
}
const panelStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 600,
  width: '100%',
  padding: 20,
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.25)',
  marginTop: 24
}
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#334155' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }
const fieldRowStyle: React.CSSProperties = { marginBottom: 12 }
const noteStyle: React.CSSProperties = { fontSize: 13, color: '#64748b', margin: '0 0 12px' }
const dangerNoteStyle: React.CSSProperties = { fontSize: 13, color: '#991b1b', background: '#fef2f2', padding: 10, borderRadius: 8, margin: '0 0 12px' }

// Findings #85: the record's time is edited and shown in its farm's zone; a
// calendar-day value (UTC-midnight sentinel) opens as that day at 00:00 instead
// of the evening before (rehearsal: "28/09/2026 19:00" for a row of the 29th).
const toDatetimeLocal = (iso: string, timeZone: string): string => farmDateTimeInputValue(iso, timeZone)
const fromDatetimeLocal = (value: string, timeZone: string): string => {
  if (!value) return ''
  return farmWallClockToInstant(value, timeZone) ?? value
}

type Phase = 'loading' | 'form' | 'review' | 'unsynced' | 'deleted'
type Mode = 'edit' | 'move' | 'delete'

const MOVE_FIELD_LABEL_KEY: Record<string, TranslationKey> = {
  lot: 'crewCorrection.field.lotName',
  lotId: 'crewCorrection.field.lotName',
  productionUnitId: 'crewCorrection.field.productionUnit'
}

export function CrewCorrectionButton({ resource, entityId, farmId, onDone, disabled, onDelete }: Props): React.ReactNode {
  const zone = farmTimeZone(farmId)
  const t = useT()
  const principal = useStoredPrincipal()
  const canCorrect = !principal || principal.role === 'editor' || principal.role === 'admin'
  const actorNames = useActorNames()

  const fields = CREW_CORRECTION_FIELDS[resource]
  const isWater = resource === 'water-log'
  // Water rows and Biomasa counts are deleted by their host screen (findings #186: counts keep Biomasa's own Eliminar).
  const canDelete = (!isWater && resource !== 'fish-stock-log') || Boolean(onDelete)

  const [open, setOpen] = useState(false)
  // Findings #165: after a delete the host refreshes on Cerrar — or right away
  // when it removes the row (and this button) first, as Agua does.
  const pendingDoneRef = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  useEffect(() => () => {
    if (pendingDoneRef.current) {
      pendingDoneRef.current = false
      void onDoneRef.current?.()
    }
  }, [])
  const [phase, setPhase] = useState<Phase>('loading')
  const [mode, setMode] = useState<Mode>('edit')
  const [serverRow, setServerRow] = useState<ServerRow | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [moveTarget, setMoveTarget] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [conflict, setConflict] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [feedItems, setFeedItems] = useState<InventoryItem[] | null>(null)
  const [history, setHistory] = useState<CrewDataCorrectionRecord[] | null>(null)
  const [units, setUnits] = useState<UnitOption[]>([])
  const [lots, setLots] = useState<Lot[]>([])

  const unitName = (id: unknown): string => {
    if (typeof id !== 'string' || !id) return ''
    return units.find((unit) => unit.id === id)?.name ?? id
  }
  const lotName = (id: unknown): string => {
    if (typeof id !== 'string' || !id) return ''
    return lots.find((lot) => lot.id === id)?.lotKey ?? id
  }

  // Move targets: lot-scoped rows pick a "Lote · Unidad" (a lot segment and
  // the unit it is in); water rows pick a unit. The server re-checks the
  // lot's location on the row's date.
  const moveOptions = useMemo(() => {
    if (!serverRow) return [] as Array<{ value: string; label: string; current: boolean }>
    if (isWater) {
      return units.map((unit) => ({
        value: unit.id,
        label: unit.name,
        current: unit.id === serverRow.productionUnitId
      }))
    }
    return lots
      .filter((lot) => Boolean(lot.productionUnitId))
      .map((lot) => ({
        value: `${lot.id}|${lot.productionUnitId}`,
        label: `${lot.lotKey} · ${unitName(lot.productionUnitId)}`,
        current: lot.id === serverRow.lotId && lot.productionUnitId === serverRow.productionUnitId
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverRow, lots, units, isWater])

  const currentLocationLabel = (row: ServerRow): string => {
    if (isWater) return unitName(row.productionUnitId)
    const name = typeof row.lot === 'string' && row.lot ? row.lot : lotName(row.lotId)
    return `${name} · ${unitName(row.productionUnitId)}`
  }

  const changedEntries = useMemo(() => {
    const out: Array<{ field: CrewCorrectionFieldSpec; from: string; to: string }> = []
    if (!serverRow) return out
    for (const field of fields) {
      const from = readCorrectionFieldValue(serverRow, field.key)
      const to = (draft[field.key] ?? '').trim()
      if (field.kind === 'datetime') {
        const fromRounded = fromDatetimeLocal(toDatetimeLocal(from, zone), zone)
        const toIso = draft[field.key] ? fromDatetimeLocal(draft[field.key], zone) : ''
        if (toIso && toIso !== fromRounded) out.push({ field, from: fromRounded, to: toIso })
        continue
      }
      if (to === '') continue
      if (field.kind === 'decimal' || field.kind === 'integer') {
        // Compare numerically so '26.5' typed against a stored '26.50' is not
        // shown as a change (matches the server, post-#148).
        const nf = Number(from)
        const nt = Number(to)
        if (Number.isFinite(nf) && Number.isFinite(nt)) {
          if (nf !== nt) out.push({ field, from, to })
          continue
        }
      }
      if (to !== from) out.push({ field, from, to })
    }
    return out
  }, [draft, fields, serverRow])

  if (!canCorrect) return null

  const openModal = () => {
    setOpen(true)
    setPhase('loading')
    setMode('edit')
    setServerRow(null)
    setReason('')
    setMoveTarget('')
    setError(null)
    setConflict(false)
    setHistory(null)
    void fetchRow(resource, entityId)
      .then((row) => {
        const initial: Record<string, string> = {}
        for (const field of fields) {
          const value = readCorrectionFieldValue(row, field.key)
          initial[field.key] = field.kind === 'datetime' ? toDatetimeLocal(value, farmTimeZone(typeof row.farmId === 'string' ? row.farmId : farmId)) : value
        }
        setServerRow(row)
        setDraft(initial)
        setPhase('form')
        const rowFarmId = typeof row.farmId === 'string' ? row.farmId : farmId
        if (rowFarmId) {
          void api.productionUnits
            .list({ farmId: rowFarmId })
            .then((list) => setUnits(list.map((unit) => ({ id: unit.id, name: unit.name }))))
            .catch(() => setUnits([]))
          if (!isWater) {
            void api.lots
              .list({ farmId: rowFarmId })
              .then(setLots)
              .catch(() => setLots([]))
          }
        }
      })
      .catch((err) => {
        // Not on the server yet (still in this device's outbox): only a delete
        // makes sense — the copy that never synced is simply dropped.
        if (err instanceof ApiError && err.status === 404) {
          setMode('delete')
          setPhase('unsynced')
          return
        }
        setError(t('crewCorrection.error.generic'))
        setPhase('form')
      })
    void api.crewCorrections
      .list(resource, entityId)
      .then(setHistory)
      .catch(() => setHistory([]))
    if (resource === 'feed-log' && feedItems === null && farmId) {
      void api.inventoryItems
        .list({ farmId })
        .then((items) => setFeedItems(items.filter((i) => i.category === 'feed' && !isMixItem(i))))
        .catch(() => setFeedItems([]))
    }
  }

  const close = () => {
    setOpen(false)
    setSubmitting(false)
  }

  const chooseMode = (next: Mode) => {
    setMode(next)
    setError(null)
  }

  const goReview = () => {
    if (mode === 'edit' && changedEntries.length === 0) {
      setError(t('crewCorrection.error.noChange'))
      return
    }
    if (mode === 'move') {
      const option = moveOptions.find((o) => o.value === moveTarget)
      if (!option || option.current) {
        setError(t('crewCorrection.error.pickMoveTarget'))
        return
      }
    }
    if (!reason.trim()) {
      setError(t('crewCorrection.error.reasonRequired'))
      return
    }
    setError(null)
    setPhase('review')
  }

  const handleFailure = (err: unknown, action: string) => {
    setSubmitting(false)
    if (err instanceof ApiError && err.status === 409) {
      setConflict(true)
      logEvent('action', 'crew.correction.conflict', { resource, entityId, action })
      return
    }
    logEvent('error', 'crew.correction.failed', {
      resource,
      entityId,
      action,
      status: err instanceof ApiError ? err.status : 0
    })
    if (action === 'move' && err instanceof ApiError && err.status === 400) {
      setError(t('crewCorrection.error.moveRejected'))
      return
    }
    setError(t('crewCorrection.error.generic'))
  }

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    const trimmedReason = reason.trim()
    const expectedUpdatedAt = typeof serverRow?.updatedAt === 'string' ? serverRow.updatedAt : undefined

    if (mode === 'delete') {
      try {
        if (onDelete) await onDelete(trimmedReason)
        else await defaultDelete(resource, entityId, trimmedReason)
        logEvent('action', 'crew.correction.deleted', { resource, entityId })
        // Findings #155: say it is done before the host refreshes (the row, and this button, then go away).
        setSubmitting(false)
        pendingDoneRef.current = true
        setPhase('deleted')
      } catch (err) {
        handleFailure(err, 'delete')
      }
      return
    }

    if (!serverRow) return

    if (mode === 'move') {
      const [lotId, productionUnitId] = isWater ? [undefined, moveTarget] : moveTarget.split('|')
      try {
        await api.crewCorrections.move({
          resource,
          entityId,
          lotId,
          productionUnitId: productionUnitId ?? '',
          reason: trimmedReason,
          expectedUpdatedAt: expectedUpdatedAt ?? ''
        })
        logEvent('action', 'crew.correction.moved', { resource, entityId })
        close()
        onDone?.()
      } catch (err) {
        handleFailure(err, 'move')
      }
      return
    }

    const changes: Record<string, string | number> = {}
    for (const { field, to } of changedEntries) changes[field.key] = to
    try {
      await api.crewCorrections.create({ resource, entityId, changes, reason: trimmedReason, expectedUpdatedAt })
      logEvent('action', 'crew.correction.saved', {
        resource,
        entityId,
        fields: changedEntries.map((c) => c.field.key)
      })
      close()
      onDone?.()
    } catch (err) {
      handleFailure(err, 'update')
    }
  }

  const set = (key: string, v: string) => setDraft((d) => ({ ...d, [key]: v }))

  const renderInput = (field: CrewCorrectionFieldSpec) => {
    const value = draft[field.key] ?? ''
    if (field.kind === 'datetime') {
      return <input type="datetime-local" style={inputStyle} value={value} onChange={(e) => set(field.key, e.target.value)} />
    }
    if (field.kind === 'observedCause') {
      return (
        <select style={inputStyle} value={value} onChange={(e) => set(field.key, e.target.value)}>
          <option value="">{t('crewCorrection.observedCause.none')}</option>
          {OBSERVED_CAUSE_OPTIONS.map((code) => (
            <option key={code} value={code}>
              {t(MORTALITY_CAUSE_LABEL_KEY[code] ?? ('crewMode.mortality.cause.unknown' as TranslationKey))}
            </option>
          ))}
        </select>
      )
    }
    if (field.kind === 'stockMethod') {
      return (
        <select style={inputStyle} value={value} onChange={(e) => set(field.key, e.target.value)} data-testid="crew-correct-stock-method">
          {STOCK_METHOD_OPTIONS.map((code) => (
            <option key={code} value={code}>{t(STOCK_METHOD_LABEL_KEY[code])}</option>
          ))}
        </select>
      )
    }
    if (field.kind === 'feedingResponse') {
      return (
        <select style={inputStyle} value={value} onChange={(e) => set(field.key, e.target.value)} data-testid="crew-correct-feeding-response">
          <option value="">{t('feed.feedingResponse.notRecorded')}</option>
          {FEEDING_RESPONSE_OPTIONS.map((code) => (
            <option key={code} value={code}>{t(FEEDING_RESPONSE_LABEL_KEY[code])}</option>
          ))}
        </select>
      )
    }
    if (field.kind === 'feedType') {
      if (feedItems && feedItems.length > 0) {
        const options = Array.from(
          new Set(feedItems.map((i) => (i.usageFeedType?.trim() || i.name).trim()).filter(Boolean))
        )
        return (
          <select style={inputStyle} value={value} onChange={(e) => set(field.key, e.target.value)}>
            <option value="">{serverRow ? readCorrectionFieldValue(serverRow, field.key) || t('crewCorrection.feedType.none') : ''}</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )
      }
      return (
        <input
          style={inputStyle}
          value={value}
          placeholder={t('crewCorrection.feedType.loading')}
          onChange={(e) => set(field.key, e.target.value)}
        />
      )
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          style={inputStyle}
          inputMode={field.kind === 'integer' ? 'numeric' : field.kind === 'decimal' ? 'decimal' : 'text'}
          value={value}
          onChange={(e) => set(field.key, e.target.value)}
        />
        {field.unit ? <span style={{ fontSize: 13, color: '#64748b' }}>{field.unit}</span> : null}
      </div>
    )
  }

  const labelFor = (field: CrewCorrectionFieldSpec) => t(field.labelKey)
  const displayValue = (field: CrewCorrectionFieldSpec, value: string): string => {
    if (!value) return t('crewCorrection.review.empty')
    if (field.kind === 'observedCause') {
      const key = MORTALITY_CAUSE_LABEL_KEY[value]
      return key ? t(key) : value
    }
    if (field.kind === 'feedingResponse') {
      const key = FEEDING_RESPONSE_LABEL_KEY[value]
      return key ? t(key) : value
    }
    if (field.kind === 'stockMethod') {
      const key = STOCK_METHOD_LABEL_KEY[value]
      return key ? t(key) : value
    }
    if (field.kind === 'datetime') return formatStoredDateTimeAsLocal(value, zone)
    return value
  }

  // History rendering: field changes use the stream's field labels; a move
  // shows lot / unit names; a delete (only visible here if the row came back)
  // shows just the action.
  const describeChange = (change: { field: string; from: string | number | null; to: string | number | null }): string => {
    const spec = fields.find((f) => f.key === change.field)
    if (spec) {
      return `${labelFor(spec)}: ${displayValue(spec, change.from === null ? '' : String(change.from))} → ${displayValue(spec, change.to === null ? '' : String(change.to))}`
    }
    const labelKey = MOVE_FIELD_LABEL_KEY[change.field]
    const label = labelKey ? t(labelKey) : change.field
    const show = (value: string | number | null) =>
      change.field === 'productionUnitId' ? unitName(value) : change.field === 'lotId' ? lotName(value) : String(value ?? '')
    return `${label}: ${show(change.from) || t('crewCorrection.review.empty')} → ${show(change.to) || t('crewCorrection.review.empty')}`
  }

  const renderHistory = () => {
    if (history === null) return null
    if (history.length === 0) {
      return <p style={noteStyle} data-testid="crew-correct-history-empty">{t('crewCorrection.history.empty')}</p>
    }
    return (
      <details style={{ marginBottom: 14 }} data-testid="crew-correct-history">
        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155' }}>
          {t('crewCorrection.history.title', { count: history.length })}
        </summary>
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {history.map((entry) => (
            <li key={entry.id} style={{ borderTop: '1px solid #e2e8f0', padding: '8px 0', fontSize: 13 }}>
              <div style={{ color: '#0f172a', fontWeight: 600 }}>
                {formatStoredDateTimeAsLocal(entry.correctedAt, zone)} · {actorLabel(actorNames, entry.correctedByActorId)} ·{' '}
                {t(`crewCorrection.history.action.${entry.action ?? 'update'}` as TranslationKey)}
              </div>
              {(entry.changes ?? [])
                .filter((change) => change.field !== 'lotId')
                .map((change) => (
                  <div key={change.field} style={{ color: '#334155' }}>{describeChange(change)}</div>
                ))}
              <div style={{ color: '#64748b', fontStyle: 'italic' }}>
                {t('crewCorrection.reason.label')}: {entry.reason}
              </div>
            </li>
          ))}
        </ul>
      </details>
    )
  }

  const modeButton = (value: Mode, labelKey: TranslationKey, testId: string) => (
    <button
      type="button"
      onClick={() => chooseMode(value)}
      data-testid={testId}
      aria-pressed={mode === value}
      style={{
        flex: 1,
        padding: '8px 10px',
        borderRadius: 8,
        border: mode === value ? '2px solid #0f766e' : '1px solid #cbd5e1',
        background: mode === value ? '#f0fdfa' : '#fff',
        fontWeight: mode === value ? 700 : 500,
        fontSize: 13,
        cursor: 'pointer'
      }}
    >
      {t(labelKey)}
    </button>
  )

  const reasonInput = (
    <div style={fieldRowStyle}>
      <label style={labelStyle}>{t('crewCorrection.reason.labelRequired')}</label>
      <input
        style={inputStyle}
        value={reason}
        placeholder={t(mode === 'delete' ? 'crewCorrection.reason.deletePlaceholder' : 'crewCorrection.reason.placeholder')}
        onChange={(e) => setReason(e.target.value)}
        data-testid="crew-correct-reason"
      />
    </div>
  )

  const renderRowSummary = (row: ServerRow) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
      <tbody>
        <tr style={{ borderTop: '1px solid #e2e8f0' }}>
          <td style={{ padding: 6 }}>{t(isWater ? 'crewCorrection.field.productionUnit' : 'crewCorrection.field.lot')}</td>
          <td style={{ padding: 6, fontWeight: 600 }}>{currentLocationLabel(row)}</td>
        </tr>
        {fields
          .map((field) => ({ field, value: readCorrectionFieldValue(row, field.key) }))
          .filter(({ value }) => value !== '')
          .map(({ field, value }) => (
            <tr key={field.key} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: 6 }}>{labelFor(field)}</td>
              <td style={{ padding: 6, fontWeight: 600 }}>{displayValue(field, value)}</td>
            </tr>
          ))}
      </tbody>
    </table>
  )

  const renderForm = () => (
    <>
      {renderHistory()}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }} role="group" aria-label={t('crewCorrection.title')}>
        {modeButton('edit', 'crewCorrection.mode.edit', 'crew-correct-mode-edit')}
        {modeButton('move', isWater ? 'crewCorrection.mode.moveUnit' : 'crewCorrection.mode.move', 'crew-correct-mode-move')}
        {canDelete ? modeButton('delete', 'crewCorrection.mode.delete', 'crew-correct-mode-delete') : null}
      </div>
      {mode === 'edit'
        ? fields.map((field) => (
            <div key={field.key} style={fieldRowStyle}>
              <label style={labelStyle}>{labelFor(field)}</label>
              {renderInput(field)}
            </div>
          ))
        : null}
      {mode === 'move' && serverRow ? (
        <>
          <p style={noteStyle}>
            {t('crewCorrection.move.current')}: <strong>{currentLocationLabel(serverRow)}</strong>
          </p>
          <div style={fieldRowStyle}>
            <label style={labelStyle}>{t(isWater ? 'crewCorrection.move.targetUnit' : 'crewCorrection.move.targetLot')}</label>
            <select
              style={inputStyle}
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
              data-testid="crew-correct-move-target"
            >
              <option value="">{t('crewCorrection.move.pick')}</option>
              {moveOptions.map((option) => (
                <option key={option.value} value={option.value} disabled={option.current}>
                  {option.current ? `${option.label} (${t('crewCorrection.move.currentTag')})` : option.label}
                </option>
              ))}
            </select>
          </div>
          {!isWater ? <p style={noteStyle}>{t('crewCorrection.move.guardHint')}</p> : null}
        </>
      ) : null}
      {mode === 'delete' && serverRow ? (
        <>
          <p style={noteStyle} data-testid="crew-correct-delete-steps">{t('crewCorrection.delete.steps')}</p>
          <p style={dangerNoteStyle}>{t('crewCorrection.delete.warning')}</p>
          {renderRowSummary(serverRow)}
        </>
      ) : null}
      {reasonInput}
      {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <button type="button" onClick={close}>
          {t('crewCorrection.button.cancel')}
        </button>
        <button type="button" onClick={goReview} data-testid="crew-correct-review">
          {t(mode === 'delete' ? 'crewCorrection.delete.review' : 'crewCorrection.button.review')}
        </button>
      </div>
    </>
  )

  const renderReview = () => {
    const moveOption = moveOptions.find((o) => o.value === moveTarget)
    return (
      <>
        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
          {t(mode === 'delete' ? 'crewCorrection.delete.reviewTitle' : 'crewCorrection.review.title')}
        </h4>
        {mode === 'delete' ? (
          <>
            <p style={dangerNoteStyle}>{t('crewCorrection.delete.warning')}</p>
            {serverRow ? renderRowSummary(serverRow) : <p style={noteStyle}>{t('crewCorrection.unsynced.note')}</p>}
          </>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 6 }}>{t('crewCorrection.review.field')}</th>
                <th style={{ textAlign: 'left', padding: 6 }}>{t('crewCorrection.review.priorValue')}</th>
                <th style={{ textAlign: 'left', padding: 6 }}>{t('crewCorrection.review.newValue')}</th>
              </tr>
            </thead>
            <tbody>
              {mode === 'move' && serverRow ? (
                <tr style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 6 }}>{t(isWater ? 'crewCorrection.field.productionUnit' : 'crewCorrection.field.lot')}</td>
                  <td style={{ padding: 6, color: '#64748b' }}>{currentLocationLabel(serverRow)}</td>
                  <td style={{ padding: 6, fontWeight: 600 }}>{moveOption?.label ?? ''}</td>
                </tr>
              ) : (
                changedEntries.map(({ field, from, to }) => (
                  <tr key={field.key} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 6 }}>{labelFor(field)}</td>
                    <td style={{ padding: 6, color: '#64748b' }}>{displayValue(field, from)}</td>
                    <td style={{ padding: 6, fontWeight: 600 }}>{displayValue(field, to)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
        <p style={{ fontSize: 13, marginTop: 12 }}>
          <strong>{t('crewCorrection.reason.label')}:</strong> {reason}
        </p>
        {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
          <button type="button" disabled={submitting} onClick={() => setPhase(serverRow ? 'form' : 'unsynced')}>
            {t('crewCorrection.button.back')}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={submit}
            data-testid="crew-correct-confirm"
            style={mode === 'delete' ? { backgroundColor: '#dc2626', color: 'white', borderColor: '#b91c1c' } : undefined}
          >
            {t(mode === 'delete' ? 'crewCorrection.delete.confirm' : 'crewCorrection.button.confirm')}
          </button>
        </div>
      </>
    )
  }

  const renderDeleted = () => (
    <>
      <p role="status" style={{ fontSize: 14, color: '#065f46', background: '#ecfdf5', padding: 12, borderRadius: 8 }} data-testid="crew-correct-deleted">
        {t('crewCorrection.delete.done')}
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button
          type="button"
          onClick={() => {
            pendingDoneRef.current = false
            close()
            onDone?.()
          }}
          data-testid="crew-correct-deleted-close"
        >
          {t('crewCorrection.button.close')}
        </button>
      </div>
    </>
  )

  const renderUnsynced = () => (
    <>
      <p style={noteStyle}>{t('crewCorrection.unsynced.note')}</p>
      {canDelete ? (
        <>
          {reasonInput}
          {error ? <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p> : null}
        </>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
        <button type="button" onClick={close}>
          {t('crewCorrection.button.close')}
        </button>
        {canDelete ? (
          <button type="button" onClick={goReview} data-testid="crew-correct-review">
            {t('crewCorrection.mode.delete')}
          </button>
        ) : null}
      </div>
    </>
  )

  return (
    <>
      <button type="button" disabled={disabled} onClick={openModal} data-testid={`crew-correct-${resource}-${entityId}`}>
        {t('crewCorrection.button')}
      </button>

      {open ? (
        <div style={overlayStyle} role="dialog" aria-modal="true" aria-label={t('crewCorrection.title')}>
          <div style={panelStyle}>
            <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>{t('crewCorrection.title')}</h3>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
              {t(`crewCorrection.resource.${resource}` as TranslationKey)}
            </p>

            {phase === 'loading' ? (
              <p style={{ fontSize: 14, color: '#64748b' }}>…</p>
            ) : conflict ? (
              <>
                <p style={{ fontSize: 14, color: '#b45309', background: '#fffbeb', padding: 12, borderRadius: 8 }}>
                  {t('crewCorrection.error.conflict')}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                  <button type="button" onClick={close}>
                    {t('crewCorrection.button.close')}
                  </button>
                </div>
              </>
            ) : phase === 'deleted' ? (
              renderDeleted()
            ) : phase === 'review' ? (
              renderReview()
            ) : phase === 'unsynced' ? (
              renderUnsynced()
            ) : (
              renderForm()
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}
